import { useMemo } from 'react';
import { isButtonEnabled, isButtonHidden } from 'src/components/design-system/Button';
import {
  CanCreditOveride,
  CanReceiptUseNegativeQty,
  ThreeWayMatchingPermissions,
} from 'src/constants/permissionsList';
import usePermission from 'src/hooks/usePermissions';
import { GLCodeDropdownData, GlCodesObject } from 'src/services/useGlCodesApi';
import { InvoiceLineItem, InvoiceQuantity } from 'src/services/useInvoiceLineItemsApi';
import { InvoiceBudgetPeriodResponse } from '../InvoiceDetailsContext';

export interface InvoiceLineItemError {
  productItemNum: string[];
  description: string[];
  glCd: string[];
  uom: string[];
  priceAmt: string[];
  quantityReceived: string[];
}

export interface InvoiceLineItemWarning {
  invoicedQuantities: string[];
}

export interface InvoiceLineItemInfo {
  quantityReceived: string[];
  generic: string[];
  glCd: string[];
  uom: string[];
  priceAmt: string[];
}

export const defaultInvoiceLineItemError: InvoiceLineItemError = {
  productItemNum: [],
  description: [],
  glCd: [],
  uom: [],
  priceAmt: [],
  quantityReceived: [],
};

export const defaultInvoiceLineItemWarning: InvoiceLineItemWarning = {
  invoicedQuantities: [],
};

export const defaultInvoiceLineItemInfos: InvoiceLineItemInfo = {
  quantityReceived: [],
  generic: [],
  glCd: [],
  uom: [],
  priceAmt: [],
};

const useInvoiceLineItemErrors = (
  lineItems: InvoiceLineItem[],
  isInProgress: boolean,
  glCodeOptions: GLCodeDropdownData,
  invoiceBudgetPeriods: InvoiceBudgetPeriodResponse[],
  isInvoiceEditDisabled: boolean,
  glCodesObject: GlCodesObject
) => {
  const _lineItems = useMemo(() => {
    const currentInvoicePeriodsString = invoiceBudgetPeriods
      .map((period) => period.glPeriodId)
      .sort((a, b) => a - b)
      .join(',');
    return lineItems.map((lineItem) => ({
      ...lineItem,
      invoicedQuantities: Object.values(
        (lineItem?.invoicedQuantities || [])
          ?.filter((quantity) => {
            const periodString = quantity.glPeriods.sort((a, b) => a - b).join(',');
            return currentInvoicePeriodsString === periodString;
          })
          ?.reduce<Record<string, InvoiceQuantity>>((acc, quantityItem) => {
            const key = `${quantityItem.glCode}-${quantityItem.price}`;
            if (acc[key]) {
              acc[key].quantity += quantityItem.quantity;
            } else {
              acc[key] = { ...quantityItem };
            }
            return acc;
          }, {}) || {}
      ),
    }));
  }, [invoiceBudgetPeriods, lineItems]);

  const isThreeWayMatching = usePermission({
    permissions: ThreeWayMatchingPermissions,
  });

  const CanReceiptUseNegativeQtyPermission = usePermission({
    permissions: CanReceiptUseNegativeQty,
  });

  const canCreditOveride = usePermission({
    permissions: CanCreditOveride,
  });

  const errors = useMemo(
    () =>
      _lineItems.map((lineItem) => {
        let error: InvoiceLineItemError = { ...defaultInvoiceLineItemError };
        if (!isInvoiceEditDisabled) {
          if (isButtonEnabled(lineItem.canDelete)) {
            if (!lineItem.productItemNum?.trim()) {
              error.productItemNum = ['Item # is required'];
            } else if (lineItem.productItemNum?.trim().length > 60) {
              error.productItemNum = ['Item # is more than 60 characters'];
            }
          }

          if (isButtonEnabled(lineItem.canDelete)) {
            if (!lineItem?.description?.trim?.()) {
              error.description = ['Description is required'];
            } else if (lineItem?.description?.trim?.().length > 4000) {
              error.description = ['Description is more than 4000  characters'];
            }
          }

          if (!lineItem.uom?.trim()) {
            error.uom = ['UOM is required'];
          } else if (lineItem.uom?.trim().length > 105) {
            error.uom = ['UOM is more than 105 characters'];
          }

          if (lineItem.priceAmt !== undefined && !lineItem.priceAmt && lineItem.priceAmt !== 0) {
            error.priceAmt = ['Price Amount is required'];
          }

          if (
            lineItem.quantityReceived === undefined ||
            (!lineItem.quantityReceived && lineItem.quantityReceived !== 0)
          ) {
            error.quantityReceived = [`Document quantity is required.`];
          }

          if (lineItem.quantityReceived < 0 && !CanReceiptUseNegativeQtyPermission) {
            error.quantityReceived = ['You do not have permission to use negative quantities.'];
          }

          if (!lineItem.glCd || !glCodeOptions.some((o) => o.value === lineItem.glCd)) {
            error.glCd = ['Document has line items that do not have a GL code assigned.'];
          }
        }

        return error;
      }, []),
    [_lineItems, isInvoiceEditDisabled, CanReceiptUseNegativeQtyPermission, glCodeOptions]
  );

  const warnings = useMemo(
    () =>
      _lineItems.map((lineItem) => {
        let warning: InvoiceLineItemWarning = { ...defaultInvoiceLineItemWarning };

        const possibleCreditQuantities = lineItem.invoicedQuantities?.find(
          (q) =>
            q.glCode === lineItem.glCd &&
            Number(q.price) === Number(lineItem.priceAmt) &&
            lineItem.quantityReceived < 0 &&
            Math.abs(lineItem.quantityReceived) <= Number(q.quantity)
        );

        if (
          lineItem.quantityReceived < 0 &&
          lineItem.poQty >= 0 &&
          isInProgress &&
          !possibleCreditQuantities &&
          ((lineItem.poQty === 0 && !canCreditOveride) || lineItem.poQty !== 0)
        ) {
          if (lineItem?.invoicedQuantities?.length) {
            warning.invoicedQuantities =
              lineItem?.invoicedQuantities
                ?.filter((i) => i.quantity)
                ?.map(
                  (invoicedQuantity) =>
                    `Can credit a maximum quantity of ${invoicedQuantity.quantity} at a price of ${invoicedQuantity.price} to the GL code ${invoicedQuantity.glCode} for selected period(s)`
                ) || [];
          } else {
            warning.invoicedQuantities = [
              'Line item has not been invoiced previously for selected period(s)',
            ];
          }
        }

        const possibleInvoiceQuantities = lineItem.invoicedQuantities?.find(
          (q) =>
            q.glCode === lineItem.glCd &&
            Number(q.price) === Number(lineItem.priceAmt) &&
            lineItem.quantityReceived > 0 &&
            lineItem.quantityReceived <= Math.abs(Number(q.quantity))
        );

        if (
          !possibleInvoiceQuantities &&
          isInProgress &&
          lineItem.quantityReceived > 0 &&
          lineItem.poQty < 0
        ) {
          if (lineItem?.invoicedQuantities?.length) {
            warning.invoicedQuantities =
              lineItem?.invoicedQuantities
                ?.filter((i) => i.quantity)
                ?.map(
                  (invoicedQuantity) =>
                    `Can reverse a credit at a maximum quantity of ${-invoicedQuantity.quantity} at a price of ${
                      invoicedQuantity.price
                    } to the GL code ${invoicedQuantity.glCode} for selected period(s)`
                ) || [];
          } else {
            warning.invoicedQuantities = [
              'Line item has not been credited previously for selected period(s)',
            ];
          }
        }

        if (
          lineItem.quantityReceived < 0 &&
          lineItem.poQty < 0 &&
          lineItem.quantityReceived + lineItem.previousQty < lineItem.poQty
        ) {
          warning.invoicedQuantities = [
            'Cannot credit a quantity for a negative PO qty in excess of the PO qty',
          ];
        }

        return warning;
      }),
    [canCreditOveride, isInProgress, _lineItems]
  );
  const infos = useMemo(
    () =>
      _lineItems.map((lineItem) => {
        let messages = { ...defaultInvoiceLineItemInfos };
        const glCodeGLStatus = glCodeOptions.find(
          (code) => code.code === lineItem.glCd
        )?.glApprovalStatus;
        if (!isButtonHidden(lineItem.canDelete)) {
          messages.generic = ['Newly added line item'];
        }
        if (
          !lineItem?.isNewLineItem &&
          lineItem.origInvoiceQty !== null &&
          lineItem.origInvoiceQty !== undefined &&
          lineItem.origInvoiceQty !== lineItem.quantityReceived
        ) {
          messages.quantityReceived = [
            'Original 810 Invoice quantity is not equal to the current Invoice quantity',
          ];
        }
        if (
          isThreeWayMatching &&
          lineItem.quantityReceived !== null &&
          lineItem.quantityReceived !== undefined &&
          lineItem.receivedQty !== null &&
          lineItem.receivedQty !== undefined &&
          lineItem.quantityReceived > lineItem.receivedQty
        ) {
          messages.quantityReceived = [
            ...messages.quantityReceived,
            `The document qty of ${lineItem.quantityReceived} is > qty received of ${lineItem.receivedQty}`,
          ];
        }
        if (
          !lineItem?.isNewLineItem &&
          lineItem.quantityReceived !== null &&
          lineItem.quantityReceived !== undefined &&
          lineItem.poQty !== null &&
          lineItem.poQty !== undefined &&
          lineItem.previousQty + lineItem.quantityReceived > lineItem.poQty &&
          lineItem.poQty >= 0
        ) {
          messages.quantityReceived = [
            ...messages.quantityReceived,
            `The document qty of ${lineItem.quantityReceived + lineItem.previousQty} (Current doc ${
              lineItem.quantityReceived
            } + Other Docs ${lineItem.previousQty}) > PO qty of ${lineItem.poQty}`,
          ];
        }
        if (
          isButtonHidden(lineItem.canDelete) &&
          lineItem.glCd !== null &&
          lineItem.glCd !== undefined &&
          lineItem.glCd !== lineItem.poGlCd
        ) {
          messages.glCd = [
            `GL Code assigned does not match PO GL Code: ${
              glCodesObject?.[lineItem?.poGlCd]?.label || ''
            }`,
          ];
        }
        if (
          !lineItem?.isNewLineItem &&
          lineItem.uom !== null &&
          lineItem.uom !== undefined &&
          String(lineItem.uom)?.toLowerCase() !== String(lineItem.poUom).toLowerCase()
        ) {
          messages.uom = [`UOM assigned does not match PO UOM: ${lineItem.poUom || ''}`];
        }
        if (
          !lineItem?.isNewLineItem &&
          lineItem.priceAmt !== null &&
          lineItem.priceAmt !== undefined &&
          lineItem.priceAmt > lineItem.poPrice
        ) {
          messages.priceAmt = [
            `Higher price than planned on the PO: $${Number(lineItem.poPrice).toFixed(2)}`,
          ];
        }

        if (glCodeGLStatus === 1) {
          messages.glCd = [...messages.glCd, 'GL Code always requires approval'];
        }

        if (glCodeGLStatus === 2) {
          messages.glCd = [...messages.glCd, 'GL Code is always exempt from approval'];
        }

        return messages;
      }),
    [_lineItems, glCodeOptions, glCodesObject, isThreeWayMatching]
  );

  return { errors, warnings, infos };
};

export default useInvoiceLineItemErrors;
